// utils/complaintAssignee.js
//
// Complaint assignee options -- mirrors the backend rule
// (services/complaintAssigneeRule.js, enforced on every assignment
// endpoint): an IT Support complaint's NEW assignee must be a Technician.
//
//   * Complaint = task_type_code IT_SUPPORT, or a legacy task with no task
//     type at all (the backend COALESCEs a missing type to IT_SUPPORT, and
//     the task detail page shows it as "IT Support").
//   * Options = the Technicians of the list the screen already loads (its own
//     unit / permission filtering stays as is), plus the CURRENT owner
//     whatever their role -- re-saving an unchanged legacy owner is allowed
//     by the backend, so it must stay visible and selected. No other
//     non-Technician is ever offered for a complaint.
//   * Non-complaint tasks: the screen keeps its existing options.
//
// Shared by Admin -> Assign Tasks, Admin -> Tasks (edit), Technician -> My
// Tasks (edit), the Reassign Task dialog and Manager -> Team Complaints.

export const COMPLAINT_ASSIGNEE_ERROR_CODE = "COMPLAINT_ASSIGNEE_NOT_TECHNICIAN";
export const COMPLAINT_ASSIGNEE_ERROR_MESSAGE = "An IT Support complaint can only be assigned to a Technician.";

const text = (v) => (v === null || v === undefined ? "" : String(v)).trim();

export function isComplaintTask(task) {
  if (!task) return false;
  // GET /tasks sends the server-derived flow (services/ticketFlowService.js):
  // a Developer / Data Analyst Own Ticket is typed IT_SUPPORT too, but is
  // NOT a complaint. Trust it whenever present.
  const flow = text(task.ticket_flow);
  if (flow) return flow.toUpperCase() === "IT_SUPPORT_COMPLAINT";
  const code = text(task.task_type_code) || text(task.task_type?.code) || text(task.taskTypeCode);
  if (code) return code.toUpperCase() === "IT_SUPPORT";
  const name = (text(task.task_type_name) || text(task.task_type?.name) || (typeof task.task_type === "string" ? text(task.task_type) : "")).toLowerCase();
  return !name || name.includes("it support");
}

export function userRoleKey(user) {
  const raw = text(user?.role_name) || text(user?.roleName) || text(user?.roles?.name) || text(user?.role);
  return raw.toLowerCase().replace(/[_-]+/g, " ");
}

export function roleLabel(user) {
  const key = userRoleKey(user);
  return key ? key.replace(/\b\w/g, (c) => c.toUpperCase()) : "";
}

export const isTechnicianUser = (user) => userRoleKey(user) === "technician";

export function currentOwnerId(task) {
  const v = task?.assigned_to;
  if (v && typeof v === "object") return text(v.id);
  return text(v ?? task?.assigned_to_id ?? task?.assignedTo);
}

/**
 * `users` -> the users to offer for a complaint: Technicians, plus the
 * current owner (from `users`, or from `allUsers`, or a stub built from the
 * task's own assigned_to_name) flagged `isCurrentOwnerOnly` when they are
 * not a Technician or were filtered out of `users`. Owner first.
 */
export function complaintAssigneeOptions(users, task, opts) {
  return roleAssigneeOptions(users, task, "technician", opts);
}

/**
 * Same shape for any work role ("technician" | "developer" | "data analyst"):
 * users of that role, plus the CURRENT owner (flagged isCurrentOwnerOnly)
 * when they are not one of them. Used for Developer / Data Analyst work too
 * (utils/workGroup.js).
 */
export function roleAssigneeOptions(users, task, role, { allUsers } = {}) {
  const list = Array.isArray(users) ? users : [];
  const ownerId = currentOwnerId(task);
  const members = list.filter((u) => userRoleKey(u) === role);
  if (!ownerId || members.some((u) => text(u.id) === ownerId)) return members;

  const ownerUser = [...list, ...(Array.isArray(allUsers) ? allUsers : [])].find((u) => text(u.id) === ownerId);
  const ownerName = text(ownerUser?.name) || text(task?.assigned_to_name) || text(task?.assigned_to?.name) || "Current owner";
  // Not among the offered role members (other role, or outside the screen's
  // current unit/search filter): kept only as the current owner.
  const owner = ownerUser
    ? { ...ownerUser, isCurrentOwnerOnly: true }
    : { id: ownerId, name: ownerName, role_name: "", isCurrentOwnerOnly: true };
  return [owner, ...members];
}

/** Applies the complaint rule only to complaints; other tasks keep `users`. */
export function assigneeOptionsFor(task, users, opts) {
  return isComplaintTask(task) ? complaintAssigneeOptions(users, task, opts) : users;
}

/** "Dev Kumar (current owner · Developer)" for the kept owner, else the name. */
export function assigneeOptionLabel(user, baseLabel) {
  const base = baseLabel ?? text(user?.name);
  if (!user?.isCurrentOwnerOnly) return base;
  const role = roleLabel(user);
  return `${base} (current owner${role ? ` · ${role}` : ""})`;
}

/** Message for a failed assignment; the complaint rule gets a clear text. */
export function assignmentErrorMessage(err, fallback) {
  const data = err?.response?.data || {};
  if (data.code === COMPLAINT_ASSIGNEE_ERROR_CODE) return data.error || data.message || COMPLAINT_ASSIGNEE_ERROR_MESSAGE;
  return data.error || data.message || fallback;
}
